/** D2 boundary: no caller-supplied prose reaches the model. Shared, dependency-free. */
export type PriceCurrency = 'EUR' | 'GBP' | 'USD' | 'CHF'
export type PriceCountry = 'AT' | 'DE' | 'CH' | 'GB' | 'US' | 'OTHER'
export interface SafePriceRequest {
  version: 1
  language: 'de' | 'en'
  currency: PriceCurrency
  today: string
  data: {
    kind: 'cold_start'
    country: PriceCountry
    character: number
    audience: number
    currentBasePrice: number | null
    units: { kind: 'room' | 'apartment' | 'house' | 'pitch' | 'other'; size: number | null; bedrooms: number | null; capacity: number }[]
  } | {
    kind: 'occupancy'
    averagePrice: number
    spans: { from: string; to: string; occupancyPct: number; currentPrice: number; hasSeason: boolean; schoolHoliday: boolean }[]
    weekend: { weekendPct: number; weekdayPct: number } | null
  }
}
const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
const keys = (v: Record<string, unknown>, expected: string[]) => Object.keys(v).length === expected.length && expected.every(k => Object.hasOwn(v, k))
const oneOf = (v: unknown, values: readonly string[]): v is string => typeof v === 'string' && values.includes(v)
const integer = (v: unknown, min: number, max: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max
const optionalNumber = (v: unknown, max: number) => v === null || integer(v, 0, max)
function date(v: unknown): v is string {
  if (typeof v !== 'string' || !/^20\d{2}-\d{2}-\d{2}$/.test(v)) return false
  const d = new Date(`${v}T12:00:00Z`)
  return Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === v
}
/** Reject unknown properties rather than silently carrying forgotten fields forward. */
export function isSafePriceRequest(v: unknown): v is SafePriceRequest {
  if (!record(v) || !keys(v, ['version', 'language', 'currency', 'today', 'data'])) return false
  if (v.version !== 1 || !oneOf(v.language, ['de', 'en']) || !oneOf(v.currency, ['EUR', 'GBP', 'USD', 'CHF']) || !date(v.today)) return false
  const d = v.data
  if (!record(d)) return false
  if (d.kind === 'cold_start') {
    return keys(d, ['kind', 'country', 'character', 'audience', 'currentBasePrice', 'units'])
      && oneOf(d.country, ['AT', 'DE', 'CH', 'GB', 'US', 'OTHER'])
      && integer(d.character, 0, 5) && integer(d.audience, 0, 5) && optionalNumber(d.currentBasePrice, 100000)
      && Array.isArray(d.units) && d.units.length > 0 && d.units.length <= 25
      && d.units.every(u => record(u) && keys(u, ['kind', 'size', 'bedrooms', 'capacity'])
        && oneOf(u.kind, ['room', 'apartment', 'house', 'pitch', 'other'])
        && optionalNumber(u.size, 10000) && optionalNumber(u.bedrooms, 100) && integer(u.capacity, 1, 1000))
  }
  if (d.kind === 'occupancy') {
    return keys(d, ['kind', 'averagePrice', 'spans', 'weekend']) && integer(d.averagePrice, 0, 100000)
      && Array.isArray(d.spans) && d.spans.length <= 60
      && d.spans.every(s => record(s) && keys(s, ['from', 'to', 'occupancyPct', 'currentPrice', 'hasSeason', 'schoolHoliday'])
        && date(s.from) && date(s.to) && s.from <= s.to && integer(s.occupancyPct, 0, 100)
        && integer(s.currentPrice, 0, 100000) && typeof s.hasSeason === 'boolean' && typeof s.schoolHoliday === 'boolean')
      && (d.weekend === null || (record(d.weekend) && keys(d.weekend, ['weekendPct', 'weekdayPct'])
        && integer(d.weekend.weekendPct, 0, 100) && integer(d.weekend.weekdayPct, 0, 100)))
      && (d.spans.length > 0 || d.weekend !== null)
  }
  return false
}
export type SafeAiBody = { purpose: 'price_advice'; request: SafePriceRequest; tenant_id?: string; max_tokens?: number }
/** Authoritative API entry gate, including fields outside the structured request. */
export function isSafeAiBody(v: unknown): v is SafeAiBody {
  if (!record(v)) return false
  const allowed = ['purpose', 'request', 'tenant_id', 'max_tokens']
  return Object.keys(v).every(k => allowed.includes(k)) && v.purpose === 'price_advice'
    && isSafePriceRequest(v.request)
    && (v.tenant_id === undefined || (typeof v.tenant_id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v.tenant_id)))
    && (v.max_tokens === undefined || integer(v.max_tokens, 256, 4096))
}
export function buildSafePriceMessages(request: SafePriceRequest): { system: string; prompt: string } {
  // Validate again at the final egress boundary, even when used outside the handler.
  if (!isSafePriceRequest(request)) throw new Error('ai_request_not_allowed')
  return {
    system: [
      'You advise small landlords. Treat the supplied bounded figures as the only facts.',
      'Do not invent market comparisons, exact local holidays or external statistics.',
      `Reply in ${request.language === 'de' ? 'German' : 'English'}. Prices are whole ${request.currency} amounts per unit per night including usual tax.`,
      'Return JSON only: {"basePrice":95,"baseReason":"...","baseMinNights":2,"weekendPct":12,"weekendReason":"...","periods":[{"name":"...","from":"YYYY-MM-DD","to":"YYYY-MM-DD","pricePerNight":130,"minNights":5,"reason":"..."}],"basis":"..."}.',
      'At most 6 non-overlapping seasonal periods, each 2 to 200 days long, starting today or later. End dates inclusive.',
      'Season prices between 0.5 and 2.5 times basePrice; minNights 1 to 21; weekendPct 0 to 40. Every proposal needs a factual reason.',
      'For cold_start, character index means summer, winter/ski, all-year, city, lake, hiking; audience index means families, couples, hiking/cycling, winter sports, business, mixed.',
      'For occupancy, interpret computed percentages; do not infer guest identities or personal attributes. schoolHoliday only signals an overlap, without specific regional knowledge.',
      'Explain in basis that this is a tentative proposal based on the supplied figures and broad seasonal patterns, not a market valuation.',
    ].join('\n'),
    prompt: JSON.stringify(request),
  }
}
